import type { TFunction } from 'i18next';

import type { ReportCatalogEntry } from './api';

/**
 * Wie ein Berichtswert dasteht -- F5-06.
 *
 * Die Einheit entscheidet ueber die Darstellung: Minuten werden zu Stunden,
 * Prozent bekommen ihr Zeichen, eine Bewertung eine Nachkommastelle. Diese
 * Zuordnung steht an einer Stelle, weil sie in der Tabelle, in der Kachel und
 * spaeter in jeder weiteren Ansicht dieselbe sein muss.
 */

export function formatReportValue(value: number, unit: string): string {
  switch (unit) {
    case 'minutes': {
      const hours = Math.floor(value / 60);
      const rest = Math.round(value % 60);
      return `${String(hours)}:${String(rest).padStart(2, '0')}`;
    }
    case 'percent':
      return `${value.toFixed(1).replace('.', ',')} %`;
    case 'rating':
      return value.toFixed(1).replace('.', ',');
    default:
      return String(Math.round(value));
  }
}

/**
 * Der Name eines Berichts in der Sprache des Anwenders.
 *
 * Der Katalog kommt vom Server und traegt seine Titel auf Deutsch -- ein
 * Rest aus der Zeit, als es nur eine Sprache gab. Die Uebersetzung liegt
 * deshalb hier, und der Servertext bleibt als Rueckfallebene stehen: Ein
 * Bericht, der morgen dazukommt, erscheint dann unuebersetzt statt gar
 * nicht. Ein leerer Eintrag in der Auswahlliste waere das schlechtere von
 * beidem.
 */
export function reportTitle(t: TFunction, entry: ReportCatalogEntry): string {
  const key = `reportName.${entry.key}`;
  const translated = t(key);
  return translated === key ? entry.title : translated;
}

/** Dasselbe fuer die Beschreibung unter der Ueberschrift. */
export function reportDescription(t: TFunction, entry: ReportCatalogEntry): string {
  const key = `reportDescription.${entry.key}`;
  const translated = t(key);
  return translated === key ? entry.description : translated;
}

/**
 * Fuellt Tage ohne Zeile mit null auf.
 *
 * Der Bericht gruppiert nach Tag und liefert nur Tage, an denen etwas
 * geschehen ist. Fuer eine Tabelle ist das richtig; fuer eine Linie ist es
 * falsch. Zwei Punkte mit einer Woche Abstand, die der Browser gerade
 * miteinander verbindet, sehen aus wie ein gleichmaessiger Verlauf -- und
 * genau in dieser Woche war Betriebsruhe.
 *
 * Die Grenzen kommen vom Aufrufer und nicht aus den Daten: Wer dreissig Tage
 * abgefragt hat, will dreissig Punkte sehen, auch wenn die ersten zehn leer
 * sind.
 */
export function fillDays(
  rows: readonly { key: string; value: number }[],
  since: Date,
  until: Date,
): { key: string; value: number }[] {
  const byDay = new Map(rows.map((row) => [row.key, row.value]));
  const filled: { key: string; value: number }[] = [];

  for (
    const cursor = new Date(since);
    cursor <= until;
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  ) {
    const key = isoDate(cursor);
    filled.push({ key, value: byDay.get(key) ?? 0 });
  }
  return filled;
}

/** Ein Datum als ``JJJJ-MM-TT`` -- so, wie der Bericht seine Tage schluesselt. */
export function isoDate(moment: Date): string {
  return moment.toISOString().slice(0, 10);
}

/** ``count`` Tage vor heute, auf Mitternacht UTC gesetzt. */
export function daysAgo(count: number): Date {
  const moment = new Date();
  moment.setUTCHours(0, 0, 0, 0);
  moment.setUTCDate(moment.getUTCDate() - count);
  return moment;
}
